/**
 * How a call group's countdown is worded, in one place.
 *
 * The server computes the arithmetic (server/utils/callGroupExpiry.js) and
 * sends it as `expiryState` on every conversation, so nothing here recalculates
 * a deadline — this file only turns that state into the words the banner, the
 * sidebar tag and the admin list all show. Keeping the phrasing together is
 * what stops the chat header saying "3 days" while the list beside it says
 * "in 3 days time".
 *
 * Everything returns null for a conversation that does not expire, which is
 * every conversation except a group a call made for itself, so callers render
 * nothing on null rather than having to know which those are.
 */

/** "today" / "tomorrow" / "in 5 days" — the same phrase both timers use. */
const inDays = (days) => {
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days} days`;
};

export const isInactive = (conversation) =>
    conversation?.expiryState?.status === 'inactive';

/**
 * The banner at the top of an open call group.
 *
 * Returns { tone, text, days } or null. `tone` is 'warning' while the group is
 * still active and 'danger' once it is counting down to being destroyed —
 * those are two different pieces of news and should not look alike.
 */
export const expiryBanner = (conversation) => {
    const state = conversation?.expiryState;
    if (!state) return null;

    if (state.status === 'inactive') {
        return {
            tone: 'danger',
            days: state.daysUntilDelete,
            text: `This group is inactive. It and everything shared in it will be `
                + `deleted permanently ${inDays(state.daysUntilDelete)}. `
                + `Send a message to keep it.`
        };
    }

    return {
        tone: 'warning',
        days: state.daysUntilInactive,
        text: `Call group — it goes inactive ${inDays(state.daysUntilInactive)} `
            + `if nobody messages, and is deleted ${state.purgeAfterDays} days after that.`
    };
};

/**
 * The short tag on a chat-list row, where there is room for three words.
 *
 * Only shown once there is something to warn about: a call group with its full
 * week ahead of it is just a group, and tagging every one of them would make
 * the tag mean nothing by the time it mattered.
 */
export const expiryTag = (conversation) => {
    const state = conversation?.expiryState;
    if (!state) return null;

    if (state.status === 'inactive') {
        return { tone: 'danger', label: `Deleted ${inDays(state.daysUntilDelete)}` };
    }
    if (state.daysUntilInactive <= 2) {
        return { tone: 'warning', label: `Inactive ${inDays(state.daysUntilInactive)}` };
    }
    return null;
};

/** "Deleted in 12 days" for the admin list, where the deadline is the point. */
export const deadlineLabel = (state) => {
    if (!state) return '';
    return state.status === 'inactive'
        ? `Deleted ${inDays(state.daysUntilDelete)}`
        : `Inactive ${inDays(state.daysUntilInactive)}`;
};

const groupExpiry = { isInactive, expiryBanner, expiryTag, deadlineLabel };
export default groupExpiry;
